import { HttpClient } from '../http/HttpClient';
import { HttpError } from '../http/HttpError';
import { fetchUserAttributes } from 'aws-amplify/auth';

/**
 * Form Submission API Service - Frontend service for contested case form submissions
 */
class FormSubmissionService {
    constructor(config = {}) {
        // Initialize HTTP client
        this.httpClient = new HttpClient({
            baseUrl: config.baseUrl || 'https://app.legalstream.org/api/contested-case-forms',
            onRequest: this.logRequest.bind(this),
            onResponse: this.logResponse.bind(this),
            onError: this.logError.bind(this),
        });
    }

    /**
     * Get current user ID from AWS Cognito
     * @private
     */
    async getUserId() {
        try {
            const attributes = await fetchUserAttributes();
            // Try to get user ID from sub (unique user identifier) or email
            return attributes.sub || attributes.email || 'anonymous';
        } catch (error) {
            console.warn('Failed to get user attributes:', error);
            return 'anonymous';
        }
    }

    /**
     * Transform API response to match expected structure
     * @private
     */
    _transformSubmissionResponse(apiSubmission) {
        try {
            const submissionData = apiSubmission.data || apiSubmission;

            return {
                id: submissionData.id,
                userId: submissionData.userId,
                submittedData: submissionData.submittedData || {},
                createdAt: submissionData.createdAt,
                updatedAt: submissionData.updatedAt,
            };
        } catch (error) {
            console.error('Error transforming submission response:', error);
            return {
                id: (apiSubmission.data || apiSubmission).id || null,
                userId: (apiSubmission.data || apiSubmission).userId || '',
                submittedData: (apiSubmission.data || apiSubmission).submittedData || {},
                createdAt: (apiSubmission.data || apiSubmission).createdAt,
                updatedAt: (apiSubmission.data || apiSubmission).updatedAt,
            };
        }
    }

    async getAllSubmissions(page = 0, size = 10, filters = {}) {
        try {
            const userId = await this.getUserId();
            const headers = { 'X-User-Id': userId };

            const params = new URLSearchParams({ page, size });
            if (filters.formId) params.set('formId', filters.formId);

            const apiResponse = await this.httpClient.get(`/submissions?${params}`, { headers });
            const data = apiResponse.data || apiResponse;

            return data;
        } catch (error) {
            throw this.enhanceError(error, 'Failed to load submissions');
        }
    }

    // ── Single ────────────────────────────────────────────────

    async getSubmission(id) {
        try {
            const userId = await this.getUserId();
            const headers = { 'X-User-Id': userId };

            const apiResponse = await this.httpClient.get(`/submissions/${id}`, { headers });
            const data = apiResponse.data || apiResponse;
            return this._transformSubmissionResponse(data);
        } catch (error) {
            throw this.enhanceError(error, `Failed to load submission #${id}`);
        }
    }

    /**
     * Submit form — create new submission (POST)
     */
    async submitForm(formData, wizardId) {
        try {
            const userId = await this.getUserId();
            const requestBody = {
                submittedData: formData,
                formId: wizardId,
            };

            const headers = { 'X-User-Id': userId };

            const apiResponse = await this.httpClient.post('/submissions', requestBody, { headers });
            const createdSubmission = apiResponse.data || apiResponse;
            return this._transformSubmissionResponse(createdSubmission);
        } catch (error) {
            throw this.enhanceError(error, 'Failed to submit form');
        } finally {
            console.groupEnd();
        }
    }

    /**
     * Update existing submission — edit mode (PUT)
     * Maps to: PUT /submissions/{id}
     */
    async updateSubmission(id, formData, wizardId) {
        try {
            const userId = await this.getUserId();
            const requestBody = {
                submittedData: formData,
                formId: wizardId,
            };

            const headers = { 'X-User-Id': userId };

            const apiResponse = await this.httpClient.put(`/submissions/${id}`, requestBody, { headers });
            const updatedSubmission = apiResponse.data || apiResponse;
            return this._transformSubmissionResponse(updatedSubmission);
        } catch (error) {
            throw this.enhanceError(error, `Failed to update submission #${id}`);
        }
    }

    /**
     * Validate form data
     */
    validateFormData(formData) {
        try {
            if (typeof formData !== 'object' || formData === null) {
                throw new Error('Form data must be a valid object');
            }
            JSON.stringify(formData);
            return true;
        } catch (error) {
            throw HttpError.validationError(`Invalid form data: ${error.message}`);
        }
    }

    /**
     * Logging methods
     */
    logRequest({ url, config }) {
        console.debug(`Request: ${config.method} ${url}`);

        if (config.body && config.method !== 'GET') {
            console.debug('Request body:', config.body);
        }
    }

    logResponse({ url, config, response }) {
        console.debug(`Response: ${config.method} ${url} - ${response.status}`);
    }

    logError({ url, config, error }) {
        console.error(`Error: ${config.method} ${url} - ${error.message}`);
    }

    /**
     * Enhance error with additional context
     */
    enhanceError(error, context) {
        if (error instanceof HttpError) {
            error.context = context;
            return error;
        }

        return new HttpError(
            error.status || 500,
            `${context}: ${error.message}`,
            { originalError: error }
        );
    }
}

// Default instance
const formSubmissionService = new FormSubmissionService();

// Named exports for convenience
export const submitForm = (formData, wizardId) => formSubmissionService.submitForm(formData, wizardId);
export const updateSubmission = (id, formData, wizardId) => formSubmissionService.updateSubmission(id, formData, wizardId);
export const validateFormData = (formData) => formSubmissionService.validateFormData(formData);
export const getMyAllSubmission = (page, size, filters) => formSubmissionService.getAllSubmissions(page, size, filters);
export const getFormSubmisisonData = (id) => formSubmissionService.getSubmission(id);
export { FormSubmissionService };